import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { tagApi } from '../../../api/tagApi.js';
import { NavBar } from '../../app/navBar.js';
import './tagManagementScreen.css';

// PHP serializes DateTime as { date: "YYYY-MM-DD HH:MM:SS.uuuuuu", ... }
const formatBirthday = (birthday) => {
    if (!birthday) {
        return '';
    }
    const raw = typeof birthday === 'string' ? birthday : birthday.date;
    return raw ? raw.slice(0, 10) : '';
};

const displayBanner = (message, type) => {
    const banner = document.createElement("div");
    banner.textContent = message;
    banner.classList.add("banner");

    if (type === "green") {
        banner.classList.add("success");
    } else if (type === "red") {
        banner.classList.add("failure");
    }

    document.body.appendChild(banner);

    setTimeout(() => {
        banner.style.opacity = "0";
        setTimeout(() => {
            banner.remove();
        }, 500);
    }, 2000);
};

const TagManagementScreen = () => {
    const [people, setPeople] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [newName, setNewName] = useState('');
    const [newBirthday, setNewBirthday] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [updatingID, setUpdatingID] = useState(null);
    const navigate = useNavigate();

    const loadPeople = useCallback(async () => {
        const response = await tagApi.getTaggablePeople();
        if (response.status === false && response.msg === "Forbidden") {
            navigate('/login');
            return;
        }

        if (response.status === true && response?.data?.data) {
            const sorted = [...response.data.data].sort((a, b) => a.name.localeCompare(b.name));
            setPeople(sorted);
        } else {
            displayBanner(response.msg || "Failed to load people", "red");
        }
        setIsLoading(false);
    }, [navigate]);

    useEffect(() => {
        loadPeople();
    }, [loadPeople]);

    const handleAddPerson = async (e) => {
        e.preventDefault();
        if (!newName.trim() || !newBirthday || isAdding) {
            return;
        }

        setIsAdding(true);
        const response = await tagApi.addPerson(newName.trim(), newBirthday);
        setIsAdding(false);

        if (response.status === true) {
            displayBanner(`Added ${newName.trim()}`, "green");
            setNewName('');
            setNewBirthday('');
            loadPeople();
        } else {
            displayBanner(response.msg || "Failed to add person", "red");
        }
    };

    const handleToggleActive = async (person) => {
        setUpdatingID(person.id);
        const response = await tagApi.setPersonActive(person.id, !person.active);
        setUpdatingID(null);

        if (response.status === true) {
            setPeople(prev => prev.map(p => (p.id === person.id ? { ...p, active: !person.active } : p)));
        } else {
            displayBanner(response.msg || "Failed to update person", "red");
        }
    };

    return (
        <div className="tag-management-page">
            <NavBar />
            <h1>Tag Management</h1>

            <form className="tag-management-add" onSubmit={handleAddPerson}>
                <h3>Add Person</h3>
                <input
                    type="text"
                    placeholder="Name"
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                />
                <label>
                    Birthday
                    <input
                        type="date"
                        value={newBirthday}
                        onChange={e => setNewBirthday(e.target.value)}
                    />
                </label>
                <button type="submit" disabled={!newName.trim() || !newBirthday || isAdding}>
                    {isAdding ? 'Adding…' : 'Add'}
                </button>
            </form>

            <h3>People</h3>
            {isLoading ? (
                <p>Loading…</p>
            ) : (
                <table className="tag-management-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Birthday</th>
                            <th>Status</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {people.map(person => (
                            <tr key={person.id} className={person.active ? '' : 'inactive'}>
                                <td>{person.name}</td>
                                <td>{formatBirthday(person.birthday)}</td>
                                <td>{person.active ? 'Active' : 'Inactive'}</td>
                                <td>
                                    <button
                                        type="button"
                                        onClick={() => handleToggleActive(person)}
                                        disabled={updatingID === person.id}
                                    >
                                        {person.active ? 'Deactivate' : 'Activate'}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export { TagManagementScreen };
